import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import {
  AgentDetail as AgentDetailData,
  deleteAgent,
  formatJoinedDate,
  getAgentDetail,
  setAgentApproval,
} from '../api/agents';
import { Listing, listingTypeLabel } from '../api/listings';
import { ListingBackState } from './ListingDetail';
import InquiryList from '../components/InquiryList';
import { extractApiError } from '../api/legalServices';

/** Strip the trailing /api so relatively-stored photo paths still resolve. */
const API_ORIGIN = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api'
).replace(/\/api\/?$/, '');

const resolvePhoto = (u: string): string =>
  /^https?:\/\//i.test(u) ? u : `${API_ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`;

const formatPrice = (price: Listing['price'], type: Listing['type']): string => {
  const n = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(n)) return '—';
  const formatted = '$' + n.toLocaleString();
  return type === 'Rental' ? `${formatted}/mo` : formatted;
};

const formatAmount = (amount: string | number | null, currency: string | null): string => {
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (amount === null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() });
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const SIGN_IN_METHOD: Record<string, string> = { google: 'Google', apple: 'Apple' };

const SUB_STATUS: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'badge badge-active' },
  trialing: { label: 'Trial', className: 'badge badge-new' },
  past_due: { label: 'Past due', className: 'badge badge-pending' },
  incomplete: { label: 'Incomplete', className: 'badge badge-pending' },
  cancelled: { label: 'Cancelled', className: 'badge badge-rejected' },
  expired: { label: 'Expired', className: 'badge badge-rejected' },
};

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-center gap-3 py-2.5 border-b border-g100 last:border-b-0">
    <span className="text-[13px] text-g500 flex-shrink-0">{label}</span>
    <span className="text-[13px] text-ink font-semibold text-right min-w-0 truncate">{children}</span>
  </div>
);

interface StatProps {
  icon: string;
  label: string;
  value: React.ReactNode;
  color: string;
  bg: string;
  delay: number;
  /** Makes the card a button (e.g. Inquiries opens its list below). */
  onClick?: () => void;
  /** Highlights the card while its list is open. */
  active?: boolean;
}

const Stat: React.FC<StatProps> = ({ icon, label, value, color, bg, delay, onClick, active }) => (
  <div
    className={
      'card flex-1 min-w-[150px] p-4 animate-fadeIn' +
      (onClick ? ' cursor-pointer hover:shadow-md' : '')
    }
    style={{
      animationDelay: `${delay}ms`,
      ...(onClick ? { transition: 'box-shadow .2s ease, border-color .2s ease' } : {}),
      ...(active ? { borderColor: color, boxShadow: `0 0 0 1px ${color}` } : {}),
    }}
    {...(onClick
      ? {
          role: 'button',
          tabIndex: 0,
          'aria-expanded': !!active,
          onClick,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          },
        }
      : {})}
  >
    <div className="flex items-center gap-2.5 mb-2">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: bg }}>
        <Icon name={icon} size={18} color={color} />
      </div>
      <div className="text-[12.5px] text-g500 font-medium">{label}</div>
      {onClick && (
        <span
          className="ml-auto w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: active ? color : 'var(--g100)',
            border: `1px solid ${active ? color : 'var(--g200)'}`,
          }}
          aria-hidden="true"
        >
          <Icon
            name={active ? 'chevron-down' : 'chevron-right'}
            size={18}
            strokeWidth={2.5}
            color={active ? '#fff' : 'var(--g700)'}
          />
        </span>
      )}
    </div>
    <div className="text-[24px] font-extrabold text-ink leading-tight">{value}</div>
  </div>
);

const AgentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [data, setData] = useState<AgentDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  // One list below the stats at a time: the Total listings and Inquiries
  // cards switch between them (listings by default).
  const [view, setView] = useState<'listings' | 'inquiries'>('listings');
  const listRef = useRef<HTMLDivElement | null>(null);
  const switched = useRef<boolean>(false);

  // A different merchant: back to their listings.
  useEffect(() => {
    setView('listings');
    switched.current = false;
  }, [id]);

  // Listing details opened from here come back to this merchant.
  const openListing = (listingId: number) => {
    const state: ListingBackState = { backTo: `/admin/agents/${id}`, backLabel: 'Back to merchant' };
    navigate(`/admin/listings/${listingId}`, { state });
  };

  const showView = (next: 'listings' | 'inquiries') => {
    switched.current = true;
    setView(next);
  };

  // Bring the list into view after the admin switches it (not on page load).
  useEffect(() => {
    if (switched.current) listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [view]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getAgentDetail(Number(id)));
    } catch (err) {
      setError(extractApiError(err, 'Failed to load merchant'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleApproval = async (desired: boolean) => {
    if (!data) return;
    setBusy(true);
    try {
      const updated = await setAgentApproval(data.agent.id, desired);
      setData((d) => (d ? { ...d, agent: { ...d.agent, ...updated } } : d));
      toast(
        (updated.name || updated.email) + ' ' + (updated.isApproved ? 'approved' : 'suspended'),
        updated.isApproved ? 'success' : 'info'
      );
    } catch (err) {
      toast(extractApiError(err, 'Failed to update merchant'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!data) return;
    setDeleting(true);
    try {
      await deleteAgent(data.agent.id);
      toast((data.agent.name || data.agent.email) + ' deleted', 'info');
      navigate('/admin/agents', { replace: true });
    } catch (err) {
      toast(extractApiError(err, 'Delete failed'), 'error');
      setDeleting(false);
    }
  };

  const backButton = (
    <button
      className="flex items-center gap-2 text-sm font-semibold text-g600 hover:text-ink transition-colors"
      onClick={() => navigate('/admin/agents')}
    >
      <Icon name="arrow-left" size={18} /> Back to merchants
    </button>
  );

  if (loading) {
    return (
      <div className="py-20 text-center text-g500">
        <Icon name="users" size={28} color="var(--g400)" /> Loading merchant...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="card p-6 max-w-[520px]">
        <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--red)' }}>
          <Icon name="x-circle" size={18} color="var(--red)" />
          <strong>Couldn't load this merchant</strong>
        </div>
        <div className="text-sm text-g600 mb-4">{error || 'Merchant not found.'}</div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/agents')}>
            Back to merchants
          </button>
        </div>
      </div>
    );
  }

  const { agent, stats, plan, subscription, listings } = data;
  const subStatus = subscription
    ? SUB_STATUS[subscription.status] || { label: subscription.status, className: 'badge badge-pending' }
    : null;

  return (
    <div className="animate-fadeIn">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        {backButton}
        <div className="flex items-center gap-2">
          {agent.isApproved ? (
            <button className="btn btn-secondary btn-sm" onClick={() => toggleApproval(false)} disabled={busy}>
              <Icon name="x-circle" size={14} /> Suspend
            </button>
          ) : (
            <button className="btn btn-success btn-sm" onClick={() => toggleApproval(true)} disabled={busy}>
              <Icon name="check-circle" size={14} color="#fff" /> Approve
            </button>
          )}
          <button
            className="btn btn-secondary btn-sm"
            style={{ color: 'var(--red)' }}
            onClick={() => setConfirmDelete(true)}
            disabled={busy}
          >
            <Icon name="x-circle" size={14} color="var(--red)" /> Delete
          </button>
        </div>
      </div>

      {/* Profile + subscription */}
      <div className="grid gap-5 lg:grid-cols-2 items-start mb-5">
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-4">
            <Avatar src={agent.avatar} name={agent.name || agent.email} id={agent.id} size={64} radius={16} />
            <div className="min-w-0 flex-1">
              <div className="text-lg font-bold text-ink truncate">
                {agent.name || <span className="text-g400 italic">Unnamed</span>}
              </div>
              <div className="text-[13px] text-g500 flex items-center gap-1.5 min-w-0">
                <Icon name="mail" size={13} color="var(--g400)" />
                <a href={`mailto:${agent.email}`} className="truncate hover:text-brand">
                  {agent.email}
                </a>
              </div>
            </div>
            <span className={'badge flex-shrink-0 ' + (agent.isApproved ? 'badge-active' : 'badge-pending')}>
              {agent.isApproved ? 'Approved' : 'Pending'}
            </span>
          </div>
          <InfoRow label="Merchant ID">#{agent.id}</InfoRow>
          <InfoRow label="Role">{agent.role === 'agent' ? 'Merchant' : agent.role}</InfoRow>
          <InfoRow label="Sign-in method">
            {agent.provider ? SIGN_IN_METHOD[agent.provider] || agent.provider : 'Email & password'}
          </InfoRow>
          <InfoRow label="Joined">{formatJoinedDate(agent.createdAt)}</InfoRow>
          <InfoRow label="Last updated">{formatDateTime(agent.updatedAt)}</InfoRow>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-2.5 mb-4">
            <Icon name="credit-card" size={20} color="var(--brand)" />
            <span className="text-base font-bold">Subscription</span>
          </div>
          <InfoRow label="Current plan">{plan.name}</InfoRow>
          <InfoRow label="Listings used">
            {stats.totalListings} / {plan.listingLimit === null ? 'Unlimited' : plan.listingLimit}
          </InfoRow>
          {subscription ? (
            <>
              <InfoRow label="Latest subscription">{subscription.planName || '—'}</InfoRow>
              <InfoRow label="Status">
                <span className={subStatus!.className}>{subStatus!.label}</span>
              </InfoRow>
              <InfoRow label="Billing">
                {formatAmount(subscription.amount, subscription.currency)}
                {subscription.billingCycle ? ` / ${subscription.billingCycle === 'yearly' ? 'year' : 'month'}` : ''}
              </InfoRow>
              <InfoRow label="Started">{formatJoinedDate(subscription.startDate || '')}</InfoRow>
              <InfoRow label={subscription.autoRenew && !subscription.cancelledAt ? 'Renews' : 'Expires'}>
                {formatJoinedDate(subscription.expiryDate || '')}
              </InfoRow>
              {subscription.cancelledAt && (
                <InfoRow label="Cancelled">{formatJoinedDate(subscription.cancelledAt)}</InfoRow>
              )}
            </>
          ) : (
            <div className="text-[13px] text-g500 pt-3">This merchant has never subscribed to a plan.</div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-4 flex-wrap mb-6">
        <Stat
          icon="building-2"
          label="Total listings"
          value={stats.totalListings}
          color="var(--brand)"
          bg="var(--brand-bg)"
          delay={0}
          onClick={() => showView('listings')}
          active={view === 'listings'}
        />
        <Stat icon="check-circle" label="Active" value={stats.activeListings} color="var(--green)" bg="var(--green-bg)" delay={40} />
        <Stat icon="x-circle" label="Inactive" value={stats.inactiveListings} color="var(--g500)" bg="var(--g100)" delay={80} />
        <Stat icon="eye" label="Total views" value={stats.totalViews.toLocaleString()} color="var(--blue)" bg="var(--blue-bg)" delay={120} />
        <Stat
          icon="message-square"
          label="Inquiries"
          value={stats.inquiries}
          color="var(--yellow)"
          bg="var(--yellow-bg)"
          delay={160}
          onClick={() => showView(view === 'inquiries' ? 'listings' : 'inquiries')}
          active={view === 'inquiries'}
        />
      </div>

      <div ref={listRef} style={{ scrollMarginTop: 16 }}>
        {view === 'inquiries' ? (
          <div className="animate-fadeIn">
            <div className="flex items-center justify-between mb-3">
              <div className="text-base font-bold text-ink">Inquiries for this merchant</div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => showView('listings')}
                aria-label="Close inquiries"
              >
                <Icon name="x" size={14} /> Close
              </button>
            </div>
            <InquiryList agentId={Number(id)} />
          </div>
        ) : (
          <div className="animate-fadeIn">
            <div className="flex items-center justify-between mb-3">
              <div className="text-base font-bold text-ink">Listings by this merchant</div>
              <div className="text-[13px] text-g500">{listings.length} total</div>
            </div>
            <div
              className="bg-white rounded-card overflow-x-auto border border-g100"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}
            >
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Property', 'Type', 'Price', 'Beds / Baths', 'Status', 'Views', 'Added', ''].map((h, i) => (
                      <th
                        key={i}
                        className={
                          'px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap' +
                          (i === 7 ? ' text-right' : '')
                        }
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {listings.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="flex flex-col items-center justify-center py-14 text-center">
                          <div
                            className="w-[64px] h-[64px] rounded-[18px] flex items-center justify-center mb-4"
                            style={{ background: 'var(--g100)' }}
                          >
                            <Icon name="building-2" size={28} color="var(--g400)" />
                          </div>
                          <div className="text-[15px] font-semibold text-ink mb-1">No listings yet</div>
                          <div className="text-sm text-g500">This merchant hasn't added any properties.</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    listings.map((l) => {
                      const thumb = l.coverPhoto || (Array.isArray(l.photos) && l.photos[0]) || null;
                      return (
                        <tr
                          key={l.id}
                          className="hover:bg-g50 transition-colors cursor-pointer"
                          onClick={() => openListing(l.id)}
                        >
                          <td className="px-4 py-3 text-sm border-b border-g100">
                            <div className="flex items-center gap-3 min-w-[240px]">
                              <div
                                className="w-14 h-11 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center"
                                style={{ background: 'var(--g100)' }}
                              >
                                {thumb ? (
                                  <img src={resolvePhoto(thumb)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <Icon name="building-2" size={18} color="var(--g400)" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-ink truncate">{l.title}</div>
                                <div className="text-xs text-g500 truncate">{l.address}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap border-b border-g100">
                            <span className={`type-badge type-${l.type.toLowerCase()}`}>
                              {listingTypeLabel(l.type)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold whitespace-nowrap border-b border-g100">
                            {formatPrice(l.price, l.type)}
                          </td>
                          <td className="px-4 py-3 text-sm text-g600 whitespace-nowrap border-b border-g100">
                            {l.beds} bd · {l.baths} ba
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap border-b border-g100">
                            <span className={'badge ' + (l.status === 'active' ? 'badge-active' : 'badge-pending')}>
                              {l.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-g600 whitespace-nowrap border-b border-g100">
                            {(l.views ?? 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-g500 whitespace-nowrap border-b border-g100">
                            {formatJoinedDate(l.createdAt || '')}
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap border-b border-g100 text-right">
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openListing(l.id);
                              }}
                            >
                              <Icon name="eye" size={14} /> Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete Merchant"
        variant="danger"
        confirmLabel="Delete"
        cancelLabel="Keep"
        busy={deleting}
        message={
          <>
            Are you sure you want to permanently delete{' '}
            <strong style={{ color: 'var(--ink)' }}>{agent.name || agent.email}</strong>?
            <br />
            Their listings and data will become orphaned. This cannot be undone.
          </>
        }
        onConfirm={doDelete}
        onCancel={() => {
          if (!deleting) setConfirmDelete(false);
        }}
      />
    </div>
  );
};

export default AgentDetail;
